import React from 'react';

interface ErrorFallbackProps {
  error?: Error | null;
  title?: string;
  message?: string;
  onRetry?: () => void;
  showDetails?: boolean;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  title = 'Something went wrong',
  message = 'We encountered an unexpected error. Please try again.',
  onRetry,
  showDetails = process.env.NODE_ENV === 'development',
}) => (
  <div className="flex items-center justify-center min-h-[400px] p-6">
    <div className="max-w-md w-full text-center">
      {/* Error Icon */}
      <div className="mx-auto w-16 h-16 mb-6 rounded-full bg-au-danger-lightest flex items-center justify-center">
        <svg
          className="w-8 h-8 text-au-danger"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>

      {/* Title */}
      <h2 className="text-h3 text-au-text-primary mb-2">{title}</h2>

      {/* Message */}
      <p className="text-au-text-secondary mb-6">{message}</p>

      {/* Error Details (development only) */}
      {showDetails && error && (
        <div className="au-alert au-alert-danger mb-6 text-left">
          <p className="font-medium mb-1">Error Details:</p>
          <p className="text-sm font-mono break-all">{error.message}</p>
          {error.stack && (
            <details className="mt-2">
              <summary className="cursor-pointer text-sm hover:underline">View stack trace</summary>
              <pre className="mt-2 text-xs overflow-auto max-h-40 p-2 bg-au-surface rounded">
                {error.stack}
              </pre>
            </details>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {onRetry && (
          <button onClick={onRetry} className="au-btn au-btn-primary">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Try Again
          </button>
        )}
        <button onClick={() => window.location.reload()} className="au-btn au-btn-outline">
          Refresh Page
        </button>
      </div>
    </div>
  </div>
);

export default ErrorFallback;
